<template>
  <data-table
    :test-id-prefix="`relatorio-os-${tipo}`"
    :config="dataTableConfig"
    :data="linhasDaTela"
    :endpoint="null"
    :filterConfig="filterConfig"
    :csvButton="csvButtonConfig">
  </data-table>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { LinhaRelatorioOS, TipoRelatorioOS } from '../../composables/useRelatoriosOS';
import { osReportExplanations } from './explanations';

const props = defineProps<{
  tipo: TipoRelatorioOS;
  linhas: LinhaRelatorioOS[];
}>();

const { t, n } = useI18n();

/** null é "não se aplica" (sem par, uma OS só) — um traço, não um zero. */
function ouTraco(formato: 'currency' | 'decimal') {
  return (params: { value: unknown }): string =>
    params.value === null || params.value === undefined ? '—' : n(Number(params.value), formato);
}

function tipoDoServico(tipo: unknown): string {
  if (tipo === 'additional_service') return t('Adicional');
  return tipo === 'combo' ? t('Combo') : t('Serviço');
}

/** Nomes que faltam viram texto, para a linha não aparecer em branco. */
const linhasDaTela = computed(() => props.linhas.map((linha) => {
  const l = linha as unknown as Record<string, unknown>;
  if (props.tipo === 'clientes') return { ...l, cliente_nome: l.cliente_id === null ? t('Sem cliente') : l.cliente_nome };
  if (props.tipo === 'servicos') return { ...l, tipo_nome: tipoDoServico(l.tipo) };
  if (props.tipo === 'consumo') return l;
  return { ...l, usuario_nome: l.usuario_nome || t('Usuário não identificado') };
}));

const EX = computed(() => osReportExplanations(t));

const COLUNAS = computed<Record<TipoRelatorioOS, Array<Record<string, unknown>>>>(() => ({
  clientes: [
    { data: 'cliente_nome', title: t('Cliente'), width: 220, sortable: true },
    { data: 'cliente_telefone', title: t('Telefone'), width: 140, sortable: true },
    { data: 'franquia_nome', title: t('Franquia'), width: 180, sortable: true },
    { data: 'ordens', title: t('OS'), explanation: EX.value.clientes.ordens, width: 80, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'total', title: t('Total'), explanation: EX.value.clientes.total, width: 130, sortable: true, renderType: 'currency', footerType: 'sum' },
    { data: 'ticket_medio', title: t('Ticket médio'), explanation: EX.value.clientes.ticket_medio, width: 130, sortable: true, renderType: 'currency' },
    { data: 'ultima_ordem_em', title: t('Última OS'), width: 150, sortable: true, renderType: 'datetime' },
  ],
  servicos: [
    { data: 'servico_nome', title: t('Serviço'), width: 220, sortable: true },
    { data: 'tipo_nome', title: t('Tipo'), width: 100, sortable: true },
    { data: 'categoria_nome', title: t('Categoria'), width: 160, sortable: true },
    { data: 'franquia_nome', title: t('Franquia'), width: 180, sortable: true },
    { data: 'quantidade', title: t('Qtd'), explanation: EX.value.servicos.quantidade, width: 80, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'ordens', title: t('OS'), explanation: EX.value.servicos.ordens, width: 80, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'valor', title: t('Valor'), explanation: EX.value.servicos.valor, width: 130, sortable: true, renderType: 'currency', footerType: 'sum' },
    { data: 'valor_medio', title: t('Valor médio'), explanation: EX.value.servicos.valor_medio, width: 130, sortable: true, renderType: 'currency' },
  ],
  consumo: [
    { data: 'cliente_nome', title: t('Cliente'), width: 220, sortable: true },
    { data: 'cliente_telefone', title: t('Telefone'), width: 140, sortable: true },
    { data: 'franquia_nome', title: t('Franquia'), width: 180, sortable: true },
    { data: 'ordens', title: t('OS'), explanation: EX.value.consumo.ordens, width: 80, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'faixa_recorrencia', title: t('Recorrência'), explanation: EX.value.consumo.faixa_recorrencia, width: 110, sortable: true },
    { data: 'servicos', title: t('Serviços'), explanation: EX.value.consumo.servicos, width: 100, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'pares', title: t('Pares'), explanation: EX.value.consumo.pares, width: 90, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'valor', title: t('Valor'), explanation: EX.value.consumo.valor, width: 130, sortable: true, renderType: 'currency', footerType: 'sum' },
    { data: 'ticket_medio', title: t('Ticket médio'), explanation: EX.value.consumo.ticket_medio, width: 130, sortable: true, renderType: 'currency' },
    { data: 'valor_por_par', title: t('Valor por par'), explanation: EX.value.consumo.valor_por_par, width: 130, sortable: true, cellRenderer: ouTraco('currency') },
    { data: 'primeira_data', title: t('Primeira OS'), width: 120, sortable: true, renderType: 'date' },
    { data: 'ultima_data', title: t('Última OS'), width: 120, sortable: true, renderType: 'date' },
    { data: 'intervalo_medio_dias', title: t('Intervalo médio (dias)'), explanation: EX.value.consumo.intervalo_medio_dias, width: 150, sortable: true, cellRenderer: ouTraco('decimal') },
    { data: 'dias_desde_ultimo', title: t('Dias desde a última'), explanation: EX.value.consumo.dias_desde_ultimo, width: 150, sortable: true, renderType: 'integer' },
  ],
  produtividade: [
    { data: 'usuario_nome', title: t('Usuário'), width: 220, sortable: true },
    { data: 'franquia_nome', title: t('Franquia'), width: 180, sortable: true },
    { data: 'ordens', title: t('OS abertas'), explanation: EX.value.produtividade.ordens, width: 110, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'valor', title: t('Valor das OS'), explanation: EX.value.produtividade.valor, width: 130, sortable: true, renderType: 'currency', footerType: 'sum' },
    { data: 'ticket_medio', title: t('Ticket médio'), explanation: EX.value.produtividade.ticket_medio, width: 130, sortable: true, renderType: 'currency' },
    { data: 'etapas', title: t('Etapas concluídas'), explanation: EX.value.produtividade.etapas, width: 130, sortable: true, renderType: 'integer', footerType: 'sum' },
    { data: 'itens', title: t('Itens produzidos'), explanation: EX.value.produtividade.itens, width: 130, sortable: true, renderType: 'integer', footerType: 'sum' },
  ],
}));

const ARQUIVOS: Record<TipoRelatorioOS, string> = {
  clientes: 'vendas_por_cliente',
  servicos: 'vendas_por_servico',
  produtividade: 'produtividade_por_usuario',
  consumo: 'consumo_por_cliente',
};

const dataTableConfig = computed(() => ({
  tableId: `tabelaRelatorioOS_${props.tipo}`,
  filename: `${ARQUIVOS[props.tipo]}_${new Date().toISOString().split('T')[0]}`,
  columns: COLUNAS.value[props.tipo],
  pageLength: 100,
  pagination: true,
  footerLabel: t('Totais'),
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
}));

const csvButtonConfig = computed(() => ({
  name: t('Exportar'),
  filename: ARQUIVOS[props.tipo],
}));
</script>
